import { SolarPanelParameters } from '../../generated/graphql'
import { subtractItems } from '../../util/array-utility'
import {
  createSecondaryWaste,
  EnergyType,
  Quadrant,
  SecondaryEnergyFlow,
  WasteType,
} from '../EnergyFlow'
import { LimitedResourceProvider } from './common/LimitedResourceProvider'
import { Provider } from './common/Provider'

/**
 * At maximum, provides the input energy.
 * If demand is less than provided, the rest will be waste.
 */
export class SolarPanelProvider extends LimitedResourceProvider<SolarPanelParameters> {
  public readonly providedEnergyTypes = [EnergyType.Electricity]

  /**
   * The overproduction that was used by batteries must be deducted from solar panel
   * waste energy flow and added to provided flow
   */
  public fixOverproduction(requirementFlow: SecondaryEnergyFlow): void {
    if (!this.flows) {
      throw new Error('Can not fix overproduction for nonexistent flows')
    }
    const { providerId, energyType, hourSeries } = requirementFlow

    const updatedFlows = this.flows.map((flow) => {
      if (flow.quadrant === Quadrant.Waste) {
        return {
          ...flow,
          hourSeries: subtractItems(flow.hourSeries, hourSeries),
        }
      }
      return flow
    })
    const provided = createSecondaryWaste(
      providerId,
      energyType,
      hourSeries,
      WasteType.Recycled,
    )
    updatedFlows.push(provided)

    this.flows = updatedFlows
  }

  protected getInitialAvailable(): number[] {
    const ratedSolarPanelPowerDensityValues = this.hourSeries[0].values

    return ratedSolarPanelPowerDensityValues.map(
      (powerDensity) =>
        (powerDensity *
          this.parameters.area *
          this.parameters.solarEfficiency) /
        this.parameters.ratingEfficiency,
    )
  }
}

export function isSolarPanelProvider(
  provider: Provider,
): provider is SolarPanelProvider {
  return provider instanceof SolarPanelProvider
}
